import { PERMISSIONS, terraContract } from '@terra/shared-authorization';
import {
  WEB_PAGES_PATH_NAME,
  WEBPAGE_RELATED_ENDPOINTS_DEFAULT_LIMIT,
  WEBPAGE_RELATED_ENDPOINTS_MAX_LIMIT,
} from './web-pages.constants';
import {
  WebPagesParamsSchema,
  WebPagesQuerySchema,
  WebPageWithSignedScreenshotReturnSchema,
  WebPageIdParamsSchema,
  ErrorResponseSchema,
  FeatureRelatedRequestsParamsSchema,
  FeatureRelatedRequestsQuerySchema,
  FeatureRelatedRequestsResponseSchema,
  ExecuteFeatureOverrideParamsSchema,
  ExecuteFeatureOverrideBodySchema,
  ExecuteFeatureOverrideResponseSchema,
  DeleteResponseSchema,
  TriggerUpdateWebpagesBodySchema,
  TriggerUpdateWebpagesResponseSchema,
  CountUntrackedPagesParamsSchema,
  CountUntrackedPagesResponseSchema,
  CountTotalPagesParamsSchema,
  CountTotalPagesResponseSchema,
  EnrichWebpagesBodySchema,
  EnrichWebpagesResponseSchema,
  TriggerFeatureAnalysisBodySchema,
  TriggerFeatureAnalysisResponseSchema,
  GenerateWebpageDescriptionParamsSchema,
  GenerateWebpageDescriptionResponseSchema,
  ReplaceWebpageScreenshotBodySchema,
  WebpageEndpointsQuerySchema,
  WebpageEndpointsReturnSchema,
  GetWebPageFeaturesResponseSchema,
} from './web-pages.schema';
import { z } from 'zod';

export const WebPagesContract = terraContract
  .prefix(`/${WEB_PAGES_PATH_NAME}`)
  .router({
    getWebPages: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/{applicationId}',
        summary: 'Get web pages by application id with optional URL filter',
        inputStructure: 'detailed',
      })
      .input(
        z.object({ params: WebPagesParamsSchema, query: WebPagesQuerySchema }),
      )
      .output(
        z.object({
          webPages: WebPageWithSignedScreenshotReturnSchema.array(),
          totalCount: z
            .int()
            .nonnegative()
            .optional()
            .describe('Total number of webpages matching the filter'),
        }),
      ),
    getWebPageById: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({ method: 'GET', path: '/id/{id}', inputStructure: 'detailed' })
      .input(z.object({ params: WebPageIdParamsSchema }))
      .output(WebPageWithSignedScreenshotReturnSchema)
      .errors({ NOT_FOUND: { status: 404, data: ErrorResponseSchema } }),
    getWebpageEndpoints: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/id/{id}/endpoints',
        summary: 'Get endpoints related to a webpage',
        description: `Retrieves API endpoints observed while visiting this webpage during crawling or traffic capture. Defaults to ${WEBPAGE_RELATED_ENDPOINTS_DEFAULT_LIMIT} items (max ${WEBPAGE_RELATED_ENDPOINTS_MAX_LIMIT}).`,
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: WebPageIdParamsSchema,
          query: WebpageEndpointsQuerySchema,
        }),
      )
      .output(WebpageEndpointsReturnSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getWebPageFeatures: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/id/{id}/features',
        summary: 'Get discovered features for a web page',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: WebPageIdParamsSchema }))
      .output(GetWebPageFeaturesResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    getFeatureRelatedRequests: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'GET',
        path: '/id/{id}/features/{featureId}/related-requests',
        summary: 'Get related requests for a web page feature',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: FeatureRelatedRequestsParamsSchema,
          query: FeatureRelatedRequestsQuerySchema,
        }),
      )
      .output(FeatureRelatedRequestsResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    executeFeatureOverride: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/id/{id}/features/{featureId}/execute-override',
        summary:
          'Execute one web page feature with an optional policy override',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: ExecuteFeatureOverrideParamsSchema,
          body: ExecuteFeatureOverrideBodySchema,
        }),
      )
      .output(ExecuteFeatureOverrideResponseSchema)
      .errors({
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        FORBIDDEN: { status: 403, data: ErrorResponseSchema },
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    deleteWebPage: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'DELETE',
        path: '/{id}',
        summary: 'Delete a web page',
        inputStructure: 'detailed',
        successStatus: 204,
      })
      .input(z.object({ params: WebPageIdParamsSchema }))
      .output(DeleteResponseSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
    triggerUpdateWebpages: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/internal/events/update-webpages',
        summary: 'Trigger webpages update after manual tracking stops',
        description:
          'Internal endpoint that triggers the webpages update process by publishing a WEB_PAGES_UPDATE_EVENT to the message broker.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: TriggerUpdateWebpagesBodySchema }))
      .output(TriggerUpdateWebpagesResponseSchema),
    countUntrackedPages: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/internal/count-untracked',
        summary: 'Count untracked pages for application and credential',
        description:
          'Internal endpoint that returns the count of pages without a webpage_id for a specific application and credential.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: CountUntrackedPagesParamsSchema }))
      .output(CountUntrackedPagesResponseSchema),
    countTotalPages: terraContract
      .meta({
        permissions: [PERMISSIONS.VIEW_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/internal/count-total',
        summary: 'Count total pages for application and credential',
        description:
          'Internal endpoint that returns the total count of pages for a specific application and credential.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: CountTotalPagesParamsSchema }))
      .output(CountTotalPagesResponseSchema),
    enrichWebpages: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/internal/enrich',
        summary: 'Enrich webpages that need enrichment data',
        description:
          'Enriches webpages that have 0 cookies, 0 links, and 0 headers. Optionally accepts a list of webpage IDs to enrich specific webpages.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: EnrichWebpagesBodySchema }))
      .output(EnrichWebpagesResponseSchema),
    triggerFeatureAnalysis: terraContract
      .meta({
        permissions: [PERMISSIONS.EXECUTE_TESTS.name],
      })
      .route({
        method: 'POST',
        path: '/internal/feature-analysis',
        summary: 'Trigger feature analysis for application webpages',
        description:
          'Triggers feature analysis for all webpages of an application. Restricted to Terra tenant users only.',
        inputStructure: 'detailed',
      })
      .input(z.object({ body: TriggerFeatureAnalysisBodySchema }))
      .output(TriggerFeatureAnalysisResponseSchema)
      .errors({ FORBIDDEN: { status: 403, data: ErrorResponseSchema } }),
    generateWebpageDescription: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'POST',
        path: '/internal/generate-description/{webpageId}',
        summary: 'Generate description for a specific webpage',
        description:
          'Triggers AI-based description generation for a specific webpage using its screenshot. Updates the webpage with the generated description.',
        inputStructure: 'detailed',
      })
      .input(z.object({ params: GenerateWebpageDescriptionParamsSchema }))
      .output(GenerateWebpageDescriptionResponseSchema)
      .errors({ NOT_FOUND: { status: 404, data: ErrorResponseSchema } }),
    replaceWebpageScreenshot: terraContract
      .meta({
        permissions: [PERMISSIONS.MANAGE_APPLICATIONS.name],
      })
      .route({
        method: 'PUT',
        path: '/id/{id}/screenshot',
        summary: 'Replace webpage screenshot',
        description:
          'Uploads and replaces the screenshot for a webpage and its linked page.',
        inputStructure: 'detailed',
      })
      .input(
        z.object({
          params: WebPageIdParamsSchema,
          body: ReplaceWebpageScreenshotBodySchema,
        }),
      )
      .output(WebPageWithSignedScreenshotReturnSchema)
      .errors({
        NOT_FOUND: { status: 404, data: ErrorResponseSchema },
        BAD_REQUEST: { status: 400, data: ErrorResponseSchema },
        INTERNAL_SERVER_ERROR: { status: 500, data: ErrorResponseSchema },
      }),
  });
